import { ExpandLess, ExpandMore, KeyboardArrowRight } from '@mui/icons-material';
import TopicIcon from '@mui/icons-material/Topic';
import { Box, Button, CircularProgress, Collapse, Divider, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { RemoteDeviceSystemInfo } from '../../../redux/RobotDataSlice';
import { ApplicationActions, RobotActions } from '../../../utils/constants';
import { formatFirmwareVersion } from '../../../utils/formatFirmwareVersion';
import SwerveHeaderIcon from '../../Icons/Mechanisms/swerve-mechanism.svg?react';
import SwerveIcon from '../assets/Swerve_simplified.svg';

type FirmwareTriplet = { major: number; minor: number; revision: number };
export type FirmwareVersionsShape = {
  sku?: string;
  revision?: number;
  versionsByType?: {
    bootloader?: FirmwareTriplet;
    main_app?: FirmwareTriplet;
    coprocessor?: FirmwareTriplet;
  };
};

interface DriveUnitInfoPanelProps {
  colorMode: string | undefined;
  expanded: boolean;
  onToggle: () => void;
}

const DriveUnitInfoPanel = ({ colorMode, expanded, onToggle }: DriveUnitInfoPanelProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const [firmwareExpanded, setFirmwareExpanded] = useState(false);

  const { uid, driveUnitFirmwareVersions, sku } = useAppSelector((state) => state.robotData);
  const firmwareUpdateState = useAppSelector((state) => state.robot.firmware.firmwareState);
  const firmwareVersion = driveUnitFirmwareVersions as unknown as FirmwareVersionsShape | null;
  const remoteDeviceSystemInfo = useAppSelector((state) => state.robotData.remoteDeviceSystemInfo);
  const driveBaseNordic = remoteDeviceSystemInfo?.find((device: RemoteDeviceSystemInfo) => device.deviceIndex === 0);
  const driveBaseSwerve1 = remoteDeviceSystemInfo?.find((device: RemoteDeviceSystemInfo) => device.deviceIndex === 1);
  const driveBaseSwerve2 = remoteDeviceSystemInfo?.find((device: RemoteDeviceSystemInfo) => device.deviceIndex === 2);
  const driveBaseSwerve3 = remoteDeviceSystemInfo?.find((device: RemoteDeviceSystemInfo) => device.deviceIndex === 3);
  const driveBaseSwerve4 = remoteDeviceSystemInfo?.find((device: RemoteDeviceSystemInfo) => device.deviceIndex === 4);
  const hasDriveUnitFirmwareMetadata = firmwareVersion != null;
  const hasCompleteRemoteSwerveDeviceInfo = [driveBaseNordic, driveBaseSwerve1, driveBaseSwerve2, driveBaseSwerve3, driveBaseSwerve4].every(Boolean);
  const isFirmwareTransferActive =
    firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.START_DOWNLOAD || firmwareUpdateState === RobotActions.FIRMWARE_UPDATE.STARTED;
  const canRenderDriveBaseDetailsDuringTransfer =
    isFirmwareTransferActive &&
    (hasDriveUnitFirmwareMetadata || [driveBaseNordic, driveBaseSwerve1, driveBaseSwerve2, driveBaseSwerve3, driveBaseSwerve4].some(Boolean));
  const isLoadingSwerveSectionData =
    expanded && !canRenderDriveBaseDetailsDuringTransfer && (!hasDriveUnitFirmwareMetadata || !hasCompleteRemoteSwerveDeviceInfo);

  const handleDULogsClick = () => {
    dispatch(applicationSlice.actions.onOpenDialog(ApplicationActions.Dialogs.DU_LOGS));
  };

  useEffect(() => {
    if (!expanded) {
      return;
    }

    window.robotService?.getRobotFirmwareVersions?.(false, true);
    if (!hasCompleteRemoteSwerveDeviceInfo) {
      window.robotService?.getNonPortRemoteDeviceSystemInfo?.();
    }
  }, [expanded, hasCompleteRemoteSwerveDeviceInfo]);

  return (
    <Paper elevation={2}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            component={SwerveHeaderIcon}
            aria-hidden
            sx={{
              width: 22,
              height: 22,
              display: 'block',
              color: 'text.primary',
              flexShrink: 0,
            }}
          />
          <Typography variant="subHeaderTitle" sx={{ fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px' }}>
            <Trans i18nKey="connectedDrawer.drive-base.title">Drive Base</Trans>
          </Typography>
        </Stack>
        <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandLess /> : <KeyboardArrowRight />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box padding="24px" paddingTop={0}>
          <Divider />
          {isLoadingSwerveSectionData && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}
          <Box>
            <Stack direction="row" spacing={8} alignItems="center" mt={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={SwerveIcon}
                  alt="swerve drive robot"
                  sx={{
                    width: '150px',
                    height: '150px',
                    filter: colorMode === 'dark' ? 'invert(1)' : 'none',
                  }}
                />
              </Box>
              <Box flex="1">
                <Stack direction="column" spacing={2} alignItems="flex-start" mt={3}>
                  <Typography variant="body1">
                    <Trans i18nKey="robotInfo.device_pn">Device P/N: {{ devicePn: sku || '' }}</Trans>
                  </Typography>
                  <Typography variant="body1">
                    <Trans i18nKey="robotInfo.board_revision">Board Revision: {{ boardRevision: firmwareVersion?.revision?.toString() || '' }}</Trans>
                  </Typography>
                  {uid && (
                    <Typography variant="body1">
                      <Trans i18nKey="robotInfo.unique-id">UID: {{ uid: uid }}</Trans>
                    </Typography>
                  )}

                  <Box sx={{ width: '100%' }}>
                    <Box
                      onClick={() => setFirmwareExpanded(!firmwareExpanded)}
                      sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        <Trans i18nKey="connectedDrawer.firmware-versions.title">Firmware Versions</Trans>
                      </Typography>
                      {firmwareExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>

                    <Collapse in={firmwareExpanded}>
                      <Box sx={{ pl: 2, mt: 1 }}>
                        {firmwareVersion?.versionsByType && Object.keys(firmwareVersion.versionsByType).length > 0 && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>
                              <Trans i18nKey="connectedDrawer.firmware-type.main">Main</Trans>:
                            </strong>{' '}
                            BL{' '}
                            {formatFirmwareVersion(
                              firmwareVersion.versionsByType.bootloader?.major ?? 0,
                              firmwareVersion.versionsByType.bootloader?.minor ?? 0,
                              firmwareVersion.versionsByType.bootloader?.revision ?? 0
                            )}{' '}
                            MA{' '}
                            {formatFirmwareVersion(
                              firmwareVersion.versionsByType.main_app?.major ?? 0,
                              firmwareVersion.versionsByType.main_app?.minor ?? 0,
                              firmwareVersion.versionsByType.main_app?.revision ?? 0
                            )}{' '}
                            CP{' '}
                            {formatFirmwareVersion(
                              firmwareVersion.versionsByType.coprocessor?.major ?? 0,
                              firmwareVersion.versionsByType.coprocessor?.minor ?? 0,
                              firmwareVersion.versionsByType.coprocessor?.revision ?? 0
                            )}
                          </Typography>
                        )}

                        {driveBaseNordic?.isConnected && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Bluetooth:</strong> BL {driveBaseNordic.firmwareBootloader} MA {driveBaseNordic.firmwareMainApp}
                          </Typography>
                        )}

                        {driveBaseSwerve1?.isConnected && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Swerve 1:</strong> BL {driveBaseSwerve1.firmwareBootloader} MA {driveBaseSwerve1.firmwareMainApp}
                          </Typography>
                        )}

                        {driveBaseSwerve2?.isConnected && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Swerve 2:</strong> BL {driveBaseSwerve2.firmwareBootloader} MA {driveBaseSwerve2.firmwareMainApp}
                          </Typography>
                        )}

                        {driveBaseSwerve3?.isConnected && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Swerve 3:</strong> BL {driveBaseSwerve3.firmwareBootloader} MA {driveBaseSwerve3.firmwareMainApp}
                          </Typography>
                        )}

                        {driveBaseSwerve4?.isConnected && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Swerve 4:</strong> BL {driveBaseSwerve4.firmwareBootloader} MA {driveBaseSwerve4.firmwareMainApp}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Stack>
              </Box>
            </Stack>
            <Box>
              <Button variant="text" startIcon={<TopicIcon fontSize="small" />} onClick={handleDULogsClick}>
                <Trans i18nKey="connectedDrawer.button.drive-unit-logs">Access Drive Unit system logs</Trans>
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DriveUnitInfoPanel;
